import { ICartEntry } from 'features/cart/types';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { CartServiceMode, IHydrateCartMutation, ServiceMode } from 'generated/graphql-gateway';

const REWARD_COMBO_MARKER = 'loyalty';

export function mapServiceModeToCartServiceMode(serviceMode: ServiceMode): CartServiceMode {
  // This is the same as a switch statement, because the two enums are the same
  return (serviceMode as unknown) as CartServiceMode;
}

/**
 * This function is used to find a minimal cart entry by sanityId.
 * It first tries to find a direct match, and if not found,
 * it searches through all entries to find one with matching menuSelections.
 */
export function findMinimalCartEntryBySanityId(
  /**
   * The lookup of minimal cart entries.
   */
  minimalCartEntriesLookup: Map<string, ICartEntry>,
  sanityId: string
): ICartEntry | undefined {
  // First try direct lookup
  const directMatch = minimalCartEntriesLookup.get(sanityId);
  if (directMatch) {
    return directMatch;
  }

  // If not found, search through all entries to find one with matching menuSelections
  for (const [, minimalCartEntry] of minimalCartEntriesLookup.entries()) {
    // Check if any menuSelection has the matching sanityId
    const hasMatchingMenuSelection = minimalCartEntry.menuSelections?.some(
      selection => selection.id === sanityId
    );

    if (hasMatchingMenuSelection) {
      return minimalCartEntry;
    }
  }

  return undefined;
}

export function findRewardBenefitFromHydratedCartEntry(
  cartEntry: ILegacyCartEntry
): ILegacyCartEntry | undefined {
  if (!isHydratedReward(cartEntry)) {
    return undefined;
  }

  const recursiveFind = (cartEntries: ILegacyCartEntry[] = []): ILegacyCartEntry | undefined => {
    for (const child of cartEntries) {
      if (child.cartId !== REWARD_COMBO_MARKER) {
        return child;
      }

      const foundBenefit = recursiveFind(child.children);
      if (foundBenefit) {
        return foundBenefit;
      }
    }

    return undefined;
  };

  return recursiveFind(cartEntry.children);
}

export function findMatchingCartEntry(
  minimalCartEntries: ICartEntry[],
  cartEntry: ILegacyCartEntry
): ICartEntry | undefined {
  return minimalCartEntries.find(
    x => x.cartId === cartEntry.cartId || x.id === cartEntry.sanityId || x.id === cartEntry._id
  );
}

export function isHydratedReward(cartEntry: ILegacyCartEntry): boolean {
  return cartEntry.children.every(child => child.cartId === REWARD_COMBO_MARKER);
}

export function getHydratedEntityId(entryId: string, parentId: string) {
  const prefix = `${parentId}$`;
  // Hydration v2
  if (entryId.startsWith(prefix)) {
    return entryId;
  }
  // Hydration v1
  return `${prefix}${entryId}`;
}

export function mapHydratedCart(
  hydratedCartEntries: IHydrateCartMutation['hydrateCart']['cartEntries'],
  minimalCartEntriesLookup: Map<string, ICartEntry>
): ILegacyCartEntry[] {
  return (hydratedCartEntries ?? []).map(hydratedCartEntry => {
    const minimalCartEntry = findMinimalCartEntryBySanityId(
      minimalCartEntriesLookup,
      hydratedCartEntry.sanityId
    );

    return {
      lineId: minimalCartEntry?.cartId ?? hydratedCartEntry.lineId,
      cartId: minimalCartEntry?.cartId ?? hydratedCartEntry.lineId,
      _id: hydratedCartEntry.sanityId,
      sanityId: hydratedCartEntry.sanityId,
      name: hydratedCartEntry.name,
      type: (hydratedCartEntry.type as unknown) as ILegacyCartEntry['type'],
      quantity: hydratedCartEntry.quantity ?? 1,
      pickerSelections: {},
      posIdsConfiguration: hydratedCartEntry.posIdsConfiguration,
      discountType: hydratedCartEntry.discountType,
      vendorConfigs: hydratedCartEntry.vendorConfigs,
      offerVendorConfigs: hydratedCartEntry.offerVendorConfigs,
      url: minimalCartEntry?.url ?? '',
      image: minimalCartEntry?.image,
      isExtra: minimalCartEntry?.isExtra,
      isDonation: minimalCartEntry?.isDonation,
      children: mapHydratedCart(
        hydratedCartEntry.children as IHydrateCartMutation['hydrateCart']['cartEntries'],
        minimalCartEntriesLookup
      ),
    } as ILegacyCartEntry;
  });
}
